import { faCircleXmark as fasCircleXmark } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import autobind from "autobind-decorator";
import type CancelablePromise from "cancelable-promise";
import compact from "lodash/compact";
import type React from "react";
import type { ReactNode } from "react";
import { Form } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import type { CommonOption } from "@/shared/ui";
import { PromisePool } from "@/shared/ui";
import { FakeButton } from "./FakeButton";
import type { FormInputProps } from "./FormInput";
import { FormInput } from "./FormInput";

export interface FormSelectOption<Type = string, LabelType extends string | ReactNode = string>
	extends CommonOption<Type, LabelType> {
	// text?: string;
	disabled?: boolean;
}

export type FormSelectOptions<Type = string, LabelType extends string | ReactNode = string> = FormSelectOption<
	Type,
	LabelType
>[];

export interface FormSelectOptionsFn<Type = string, LabelType extends string | ReactNode = string> {
	id: string;
	fn: () => Promise<FormSelectOptions<Type, LabelType>> | CancelablePromise<FormSelectOptions<Type, LabelType>>;
}

export interface FormSelectProps<T = string> extends FormInputProps<T> {
	/**
	 * Placeholder text for the select input.
	 */
	placeholder?: string;
	/**
	 * The options for the select input.
	 */
	options: FormSelectOptions<T> | FormSelectOptionsFn<T>;
}

export interface FormSelectState<T = string> {
	options: FormSelectOptions<T>;
	error: boolean;
}

export class FormSelect<T = string> extends FormInput<T, FormSelectProps<T>, FormSelectState<T>> {
	private readonly promisePool = new PromisePool();
	protected lastOptionsId: string = undefined;
	protected readonly defaultValue = undefined;

	public constructor(props: FormSelectProps<T>) {
		super(props);

		this.state = {
			options: Array.isArray(props.options) ? props.options : [],
			error: false,
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		this.updateOptions();
	}

	public componentDidUpdate(prevProps: FormSelectProps<T>, prevState: FormSelectState<T>): void {
		super.componentDidUpdate(prevProps, prevState);

		this.updateOptions();
	}

	public componentWillUnmount(): void {
		super.componentWillUnmount();

		this.promisePool.cancel();
	}

	@autobind
	private updateOptions(force?: boolean): void {
		const { options } = this.props;
		const { options: stateOptions } = this.state;

		const checkNewValue = () => {
			if (!this.state.options.find((item) => item.value === this.lastValue)) {
				this.handleProtectedChange(undefined);
			}
		};

		if (!Array.isArray(options)) {
			if (force || options.id !== this.lastOptionsId) {
				this.lastOptionsId = options.id;
				this.promisePool
					.add(options.fn())
					.then((realOptions) => this.setState({ options: realOptions, error: false }, checkNewValue))
					.catch(() => {
						this.setState({
							error: true,
						});

						this.handleProtectedChange(undefined);
					});
			}
		} else if (JSON.stringify(options) !== JSON.stringify(stateOptions)) {
			this.lastOptionsId = undefined;
			this.setState(
				{
					options,
					error: false,
				},
				checkNewValue,
			);
		}
	}

	protected renderInput(fieldProps: FieldRenderProps<T>, additionalClass?: string): ReactNode {
		const { placeholder, disabled, size, readOnly, tabIndex, required } = this.props;
		const { options } = this.state;
		const {
			input: { value = undefined, name },
			meta: { invalid, touched },
		} = fieldProps;

		const className = compact([
			"custom-select",
			placeholder && "has-placeholder",
			placeholder && value === undefined && "placeholder",
			additionalClass,
		]).join(" ");

		return (
			<Form.Control
				name={name}
				className={className}
				as="select"
				size={size}
				readOnly={readOnly}
				disabled={!options?.length || disabled || readOnly}
				tabIndex={tabIndex}
				required={required}
				isInvalid={!disabled && touched && invalid}
				value={value as unknown as string}
				onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
					const element = event.currentTarget;
					const index = placeholder ? element.selectedIndex - 1 : element.selectedIndex;
					const newValue = options[index]?.value;

					this.handleChange(newValue, fieldProps);
				}}
				onFocus={(event: React.FocusEvent<HTMLSelectElement>) => {
					this.handleFocus(event, fieldProps);
				}}
				onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
					this.handleBlur(event, fieldProps);
				}}
			>
				{placeholder && (
					<option value={undefined} disabled={value !== undefined}>
						{placeholder}
					</option>
				)}
				{options.map((option) => (
					<option key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
						{option.label}
					</option>
				))}
			</Form.Control>
		);
	}

	protected renderInfo(info: ReactNode): ReactNode {
		const { error } = this.state;

		let content = info;

		if (error) {
			content = (
				<span className="text-danger">
					<FontAwesomeIcon icon={fasCircleXmark} className="mr-1" />
					Váratlan hiba az adatok betöltése közben.
					<FakeButton className="ml-2" onClick={() => this.updateOptions(true)}>
						Újratöltés!
					</FakeButton>
				</span>
			);
		}

		return super.renderInfo(content);
	}

	@autobind
	protected handleValidate(value: T, allValues: Record<string, any>): string {
		const { required } = this.props;
		const { options } = this.state;
		const valid = super.handleValidate(value, allValues);

		if (valid) {
			return valid;
		}

		if (!this.isDisabled() && required && !options.find((option) => option.value === value)) {
			return "Kérjük válassz a listából!";
		}
	}
}
